import React, { useState } from 'react';
import { formatDate, placed } from '../../../helpers/UtilityHelper';
import { Link } from 'react-router-dom';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import ProzoDataGrid from '../../../common/ProzoDataGrid';
import { LoadingButton } from '@mui/lab';
import CheckCircleIcon from '@mui/icons-material/Download';
import { apiConstants, postGeneric } from '../../../common/constants';
import { primaryTextSx, secondaryTextSx, awbTextSx, cellStackSx, getStatusChipSx, getPaymentChipSx } from '../../orders/list2/cellStyles';
import { primaryBtnSx, dataGridWrapSx } from '../../../styles/patterns';
import { formatCurrency } from '../../../helpers/UtilityHelper';

// Column set mirrors the admin panel's Bulk Tracking report (proship-backend's
// components/tracking/list/trackingListItem.js) so merchants get the same real MIS on
// export -- same underlying tracking data (waybillDetails), just presented with this
// panel's own cell styling for the columns that already had it.
const TrackingListitem = (props) => {
    const { trackingListInfo } = props;
    const listData = (trackingListInfo.waybillDetails && trackingListInfo.waybillDetails.length > 0) ? trackingListInfo.waybillDetails : [];
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(100);
    const [loading, setIsLoading] = useState(false)
    const [rowSelectionModel, setRowSelectionModel] = useState([])

    const bulkCreateHandler = () => {
        setIsLoading(true)
        postGeneric(apiConstants.GET_BULK_LABELS, {
            awbList: rowSelectionModel
        }).then(({ data }) => {
            window.open(data.mergedPdf, true)
        }).catch(e => console.error(e))
            .finally(() => setIsLoading(false))
    };

    const orderDateValue = (row) => {
        const orderStatusHistory = Array.isArray(row.order_history) ? row.order_history : [];
        const os = orderStatusHistory.find((item) => placed.includes(item.orderStatusEnum));
        if (os) return os.creationDate ? formatDate(os.creationDate, 'dd MMM, yyyy') : '';
        return row.createdDate ? formatDate(row.createdDate, 'dd MMM, yyyy') : '';
    };

    const columns = [
        {
            field: "orderId",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Order Details",
            flex: 1.2,
            minWidth: 140,
            valueGetter: (params) => params.row?.failedReason || params.row?.shippingLabelId || params.row?.orderId || '',
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Box sx={cellStackSx}>
                        <Typography sx={primaryTextSx}>{row.orderId}</Typography>
                        {row.shippingLabelId && row.reference !== row.shippingLabelId && (
                            <Typography sx={secondaryTextSx}>{row.shippingLabelId}</Typography>
                        )}
                        {row.failedReason && (
                            <Typography sx={{ ...secondaryTextSx, color: '#d32f2f' }}>{row.failedReason}</Typography>
                        )}
                    </Box>
                );
            },
        },
        {
            field: "reference", headerAlign: "left", hideSortIcons: true, headerName: "Order Number", flex: 0.8, minWidth: 110,
            valueGetter: (params) => params.row?.reference || '',
        },
        {
            field: "orderType", headerAlign: "left", hideSortIcons: true, headerName: "Shipment Type", flex: 0.8, minWidth: 120,
            valueGetter: (params) => params.row?.orderType || '',
        },
        {
            field: "order_date",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Order Date",
            flex: 0.8,
            minWidth: 100,
            valueGetter: (params) => orderDateValue(params.row),
            renderCell: (params) => (
                <Box sx={cellStackSx}>
                    <Typography sx={secondaryTextSx}>{orderDateValue(params.row)}</Typography>
                </Box>
            ),
        },
        {
            field: "waybill",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "AWB Number",
            flex: 1,
            minWidth: 130,
            valueGetter: (params) => params.row?.waybill || '',
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Box sx={cellStackSx}>
                        <Typography sx={awbTextSx}>{row.waybill || ""}</Typography>
                    </Box>
                );
            },
        },
        {
            field: "brandName", headerAlign: "left", hideSortIcons: true, headerName: "Brand Name", flex: 0.8, minWidth: 120,
            valueGetter: (params) => params.row?.brandName || '',
        },
        // Courier: parent (merchant-facing) name is the primary, meaningful one; the raw
        // `courierPartner` code and the account code are shown too since ops sometimes needs
        // the exact code, not just the friendly name -- this was previously the ONLY courier
        // field shown (as a secondary line inside the AWB cell) with no account code at all.
        {
            field: "courierPartnerParent", headerAlign: "left", hideSortIcons: true, headerName: "Courier Parent", flex: 0.8, minWidth: 120,
            valueGetter: (params) => params.row?.courierPartnerParent || params.row?.courierPartner || '',
        },
        {
            field: "courierPartner", headerAlign: "left", hideSortIcons: true, headerName: "Courier Partner", flex: 0.8, minWidth: 140,
            valueGetter: (params) => params.row?.courierPartner || '',
        },
        {
            field: "courierAccountCode", headerAlign: "left", hideSortIcons: true, headerName: "Courier Account Code", flex: 0.8, minWidth: 140,
            valueGetter: (params) => params.row?.courierAccountCode || '',
        },
        {
            field: "edd", headerAlign: "left", hideSortIcons: true, headerName: "EDD", flex: 0.7, minWidth: 100,
            valueGetter: (params) => params.row?.edd || '',
        },
        {
            field: "eddTat", headerAlign: "left", hideSortIcons: true, headerName: "EDD TAT", flex: 0.5, minWidth: 80,
            valueGetter: (params) => params.row?.eddTat ?? '',
        },
        {
            field: "zone", headerAlign: "left", hideSortIcons: true, headerName: "Zone", flex: 0.5, minWidth: 90,
            valueGetter: (params) => params.row?.zone || '',
        },
        {
            field: "pickupLocation",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Pickup",
            flex: 0.8,
            minWidth: 100,
            valueGetter: (params) => params.row?.pickupLocation || '',
            renderCell: (params) => (
                <Box sx={cellStackSx}>
                    <Typography sx={secondaryTextSx}>{params.row.pickupLocation}</Typography>
                </Box>
            ),
        },
        {
            field: "pickupDate", headerAlign: "left", hideSortIcons: true, headerName: "Pickup Date", flex: 0.7, minWidth: 100,
            valueGetter: (params) => params.row?.pickupDetails?.pickupTime ? formatDate(params.row.pickupDetails.pickupTime, 'dd MMM, yyyy') : '',
        },
        {
            field: "pickupPincode", headerAlign: "left", hideSortIcons: true, headerName: "Pickup Pincode", flex: 0.6, minWidth: 100,
            valueGetter: (params) => params.row?.pickupDetails?.from_pincode || '',
        },
        {
            field: "pickupCity", headerAlign: "left", hideSortIcons: true, headerName: "Pickup City", flex: 0.6, minWidth: 100,
            valueGetter: (params) => params.row?.pickupDetails?.from_city || '',
        },
        {
            field: "pickupState", headerAlign: "left", hideSortIcons: true, headerName: "Pickup State", flex: 0.6, minWidth: 100,
            valueGetter: (params) => params.row?.pickupDetails?.from_state || '',
        },
        {
            field: "pickupAddress", headerAlign: "left", hideSortIcons: true, headerName: "Pickup Address", flex: 1, minWidth: 180,
            valueGetter: (params) => params.row?.pickupDetails?.from_address || '',
        },
        {
            field: "pickupName", headerAlign: "left", hideSortIcons: true, headerName: "Pickup Name", flex: 0.8, minWidth: 120,
            valueGetter: (params) => params.row?.pickupDetails?.from_name || '',
        },
        {
            field: "pickupPhone", headerAlign: "left", hideSortIcons: true, headerName: "Pickup Phone", flex: 0.7, minWidth: 120,
            valueGetter: (params) => params.row?.pickupDetails?.from_phone_number || '',
        },
        {
            field: "pickupEmail", headerAlign: "left", hideSortIcons: true, headerName: "Pickup Email", flex: 0.9, minWidth: 150,
            valueGetter: (params) => params.row?.pickupDetails?.from_email || '',
        },
        {
            field: "pickupGst", headerAlign: "left", hideSortIcons: true, headerName: "Pickup GST", flex: 0.7, minWidth: 120,
            valueGetter: (params) => params.row?.pickupGst || '',
        },
        {
            field: "pickupCountry", headerAlign: "left", hideSortIcons: true, headerName: "Pickup Country", flex: 0.6, minWidth: 100,
            valueGetter: (params) => params.row?.pickupCountry || '',
        },
        {
            field: "deliveryLocation",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Delivery",
            flex: 0.8,
            minWidth: 100,
            valueGetter: (params) => params.row?.deliveryLocation || '',
            renderCell: (params) => (
                <Box sx={cellStackSx}>
                    <Typography sx={secondaryTextSx}>{params.row.deliveryLocation}</Typography>
                </Box>
            ),
        },
        {
            field: "dropPincode", headerAlign: "left", hideSortIcons: true, headerName: "Drop Pincode", flex: 0.6, minWidth: 100,
            valueGetter: (params) => params.row?.dropPincode || '',
        },
        {
            field: "dropCity", headerAlign: "left", hideSortIcons: true, headerName: "Drop City", flex: 0.6, minWidth: 100,
            valueGetter: (params) => params.row?.dropCity || '',
        },
        {
            field: "dropState", headerAlign: "left", hideSortIcons: true, headerName: "Drop State", flex: 0.6, minWidth: 100,
            valueGetter: (params) => params.row?.dropState || '',
        },
        {
            field: "dropAddress", headerAlign: "left", hideSortIcons: true, headerName: "Drop Address", flex: 1, minWidth: 180,
            valueGetter: (params) => params.row?.dropAddress || '',
        },
        {
            field: "dropName", headerAlign: "left", hideSortIcons: true, headerName: "Drop Name", flex: 0.8, minWidth: 120,
            valueGetter: (params) => params.row?.dropName || '',
        },
        {
            field: "dropPhone", headerAlign: "left", hideSortIcons: true, headerName: "Drop Phone", flex: 0.7, minWidth: 120,
            valueGetter: (params) => params.row?.dropPhone || '',
        },
        {
            field: "dropEmail", headerAlign: "left", hideSortIcons: true, headerName: "Drop Email", flex: 0.9, minWidth: 150,
            valueGetter: (params) => params.row?.dropEmail || '',
        },
        {
            field: "dropCountry", headerAlign: "left", hideSortIcons: true, headerName: "Drop Country", flex: 0.6, minWidth: 100,
            valueGetter: (params) => params.row?.dropCountry || '',
        },
        {
            field: "currentLocation", headerAlign: "left", hideSortIcons: true, headerName: "Current Location", flex: 0.9, minWidth: 140,
            valueGetter: (params) => params.row?.currentLocation || '',
        },
        {
            field: "shipment",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Weight",
            flex: 0.6,
            minWidth: 80,
            valueGetter: (params) => (params.row?.failedReason ? '' : `${params.row?.item_weight ?? ''} gms`),
            renderCell: (params) => {
                const { row } = params;
                if (row.failedReason) return null;
                return (
                    <Box sx={cellStackSx}>
                        <Typography sx={secondaryTextSx}>{row.item_weight} gms</Typography>
                    </Box>
                );
            },
        },
        {
            field: "deadWeight", headerAlign: "left", hideSortIcons: true, headerName: "Dead Weight", flex: 0.6, minWidth: 90,
            valueGetter: (params) => (params.row?.deadWeight != null ? `${params.row.deadWeight} gms` : ''),
        },
        {
            field: "shipmentDimensions", headerAlign: "left", hideSortIcons: true, headerName: "Dimensions (L×B×H)", flex: 0.8, minWidth: 140,
            valueGetter: (params) => {
                const { length, breadth, height } = params.row || {};
                return (length != null && breadth != null && height != null) ? `${length} × ${breadth} × ${height}` : '';
            },
        },
        {
            field: "paymentDetails",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Payment",
            flex: 0.8,
            minWidth: 100,
            valueGetter: (params) => (params.row?.failedReason ? '' : `${params.row?.invoiceValue ? formatCurrency(params.row.invoiceValue) : ''} ${params.row?.paymentMode || ''}`.trim()),
            renderCell: (params) => {
                const { row } = params;
                if (row.failedReason) return null;
                const paymentMode = row.paymentMode || "";
                return (
                    <Box sx={cellStackSx}>
                        <Typography sx={primaryTextSx}>
                            {row.invoiceValue ? formatCurrency(row.invoiceValue) : ""}
                        </Typography>
                        {paymentMode && <Chip size="small" variant="outlined" label={paymentMode} sx={getPaymentChipSx(paymentMode)} />}
                    </Box>
                );
            },
        },
        {
            field: "codValue", headerAlign: "left", hideSortIcons: true, headerName: "COD Value", flex: 0.5, minWidth: 90,
            valueGetter: (params) => (params.row?.codValue != null ? params.row.codValue : ''),
        },
        {
            field: "isPartialPrepaid", headerAlign: "left", hideSortIcons: true, headerName: "Partial Prepaid", flex: 0.6, minWidth: 100,
            valueGetter: (params) => (params.row?.isPartialPrepaid != null ? (params.row.isPartialPrepaid ? 'Yes' : 'No') : ''),
        },
        {
            field: "currentStatus",
            headerAlign: "left",
            hideSortIcons: true,
            headerName: "Status",
            flex: 0.9,
            minWidth: 120,
            valueGetter: (params) => (params.row?.failedReason ? '' : (params.row?.currentStatus || '')),
            renderCell: (params) => {
                const { row } = params;
                if (row.failedReason) return null;
                return (
                    <Box sx={cellStackSx}>
                        <Chip size="small" variant="outlined" label={row.currentStatus} sx={getStatusChipSx(row.currentStatus)} />
                    </Box>
                );
            },
        },
        {
            field: "latestRemarks", headerAlign: "left", hideSortIcons: true, headerName: "Latest Remarks", flex: 0.9, minWidth: 140,
            valueGetter: (params) => (params.row?.latestRemarks ? params.row.latestRemarks.replace(/[^a-zA-Z0-9\s]/g, '') : ''),
        },
        {
            field: "latestFailedDeliveryReason", headerAlign: "left", hideSortIcons: true, headerName: "Latest Failed Delivery Reason", flex: 1, minWidth: 160,
            valueGetter: (params) => params.row?.latestFailedDeliveryReason || '',
        },
        {
            field: "current_ndr", headerAlign: "left", hideSortIcons: true, headerName: "Last NDR Remark", flex: 1, minWidth: 160,
            valueGetter: (params) => {
                const history = params.row?.order_history;
                const lastFailedDelivery = [...(history ?? [])].reverse().find((item) => item?.orderStatusCode === 9);
                return lastFailedDelivery?.remark || '';
            },
        },
        {
            field: "attemptCount", headerAlign: "left", hideSortIcons: true, headerName: "Attempt Count", flex: 0.6, minWidth: 100,
            valueGetter: (params) => params.row?.order_history?.reduce((acc, item) => acc + (item?.orderStatusEnum === "OUT_FOR_DELIVERY" ? 1 : 0), 0) || 0,
        },
        {
            field: "statusDate", headerAlign: "left", hideSortIcons: true, headerName: "Last Scan Date", flex: 0.7, minWidth: 100,
            valueGetter: (params) => (params.row?.statusDate ? formatDate(params.row.statusDate, 'dd MMM, yyyy') : ''),
        },
        {
            field: "deliveredDate", headerAlign: "left", hideSortIcons: true, headerName: "Delivered Date", flex: 0.7, minWidth: 100,
            valueGetter: (params) => {
                const history = params.row?.order_history;
                const delivered = [...(history ?? [])].reverse().find((item) => item?.orderStatusEnum === 'DELIVERED');
                return delivered?.creationDate ? formatDate(delivered.creationDate, 'dd MMM, yyyy') : '';
            },
        },
        {
            field: "receivedBy", headerAlign: "left", hideSortIcons: true, headerName: "Received By", flex: 0.7, minWidth: 120,
            valueGetter: (params) => {
                const history = params.row?.order_history;
                const delivered = [...(history ?? [])].reverse().find((item) => item?.orderStatusEnum === 'DELIVERED');
                return delivered?.additional?.receiver_name || '';
            },
        },
        {
            field: "otpVerifiedDelivery", headerAlign: "left", hideSortIcons: true, headerName: "OTP Verified Delivery", flex: 0.7, minWidth: 130,
            valueGetter: (params) => {
                if (params.row?.otpVerifiedDelivery != null) return params.row.otpVerifiedDelivery ? 'Yes' : 'No';
                const history = params.row?.order_history;
                const delivered = [...(history ?? [])].reverse().find((item) => item?.orderStatusEnum === 'DELIVERED');
                if (!delivered) return '';
                return delivered.otpVerifiedDelivery === true ? 'Yes' : 'No';
            },
        },
        {
            field: "rtoAwb", headerAlign: "left", hideSortIcons: true, headerName: "RTO AWB", flex: 0.7, minWidth: 110,
            valueGetter: (params) => params.row?.rtoAwb || '',
        },
        {
            field: "rtoMarkDate", headerAlign: "left", hideSortIcons: true, headerName: "RTO Mark Date", flex: 0.7, minWidth: 120,
            valueGetter: (params) => params.row?.rtoMarkDate || '',
        },
        {
            field: "rtoDeliveryDate", headerAlign: "left", hideSortIcons: true, headerName: "RTO Delivery Date", flex: 0.7, minWidth: 130,
            valueGetter: (params) => params.row?.rtoDeliveryDate || '',
        },
        {
            field: "ndrStatus", headerAlign: "left", hideSortIcons: true, headerName: "NDR Status", flex: 0.6, minWidth: 110,
            valueGetter: (params) => params.row?.ndrStatus || '',
        },
        {
            field: "channelName", headerAlign: "left", hideSortIcons: true, headerName: "Channel Name", flex: 0.7, minWidth: 120,
            valueGetter: (params) => params.row?.channelName || '',
        },
        {
            field: "items", headerAlign: "left", hideSortIcons: true, headerName: "Items", flex: 1, minWidth: 160,
            valueGetter: (params) => params.row?.items || '',
        },
        {
            field: "productSkuCode", headerAlign: "left", hideSortIcons: true, headerName: "Product SKU Code", flex: 0.8, minWidth: 140,
            valueGetter: (params) => params.row?.productSkuCode || '',
        },
        {
            field: "itemDescription", headerAlign: "left", hideSortIcons: true, headerName: "Item Description", flex: 1, minWidth: 160,
            valueGetter: (params) => params.row?.itemDescription || '',
        },
        {
            field: "itemsQuantity", headerAlign: "left", hideSortIcons: true, headerName: "Items Quantity", flex: 0.6, minWidth: 100,
            valueGetter: (params) => params.row?.itemsQuantity ?? '',
        },
        {
            field: "invoiceNumber", headerAlign: "left", hideSortIcons: true, headerName: "Invoice Number", flex: 0.7, minWidth: 130,
            valueGetter: (params) => params.row?.invoiceNumber || '',
        },
        {
            field: "invoiceDate", headerAlign: "left", hideSortIcons: true, headerName: "Invoice Date", flex: 0.7, minWidth: 120,
            valueGetter: (params) => params.row?.invoiceDate || '',
        },
        {
            field: "merchantPrice", headerAlign: "left", hideSortIcons: true, headerName: "Merchant Price", flex: 0.6, minWidth: 120,
            valueGetter: (params) => params.row?.merchantPrice ?? '',
        },
        {
            field: "merchantPriceRto", headerAlign: "left", hideSortIcons: true, headerName: "Merchant Price (RTO)", flex: 0.6, minWidth: 140,
            valueGetter: (params) => params.row?.merchantPriceRto ?? '',
        },
        {
            field: "minTat", headerAlign: "left", hideSortIcons: true, headerName: "Min TAT", flex: 0.5, minWidth: 80,
            valueGetter: (params) => (params.row?.minTat != null ? params.row.minTat : ''),
        },
        {
            field: "deliveryOtpSent", headerAlign: "left", hideSortIcons: true, headerName: "Delivery OTP Sent", flex: 0.6, minWidth: 120,
            valueGetter: (params) => params.row?.deliveryOtpSent || '',
        },
        {
            field: "otpVerifiedCancellation", headerAlign: "left", hideSortIcons: true, headerName: "OTP Verified Cancellation", flex: 0.7, minWidth: 150,
            valueGetter: (params) => (params.row?.otpVerifiedCancellation != null ? (params.row.otpVerifiedCancellation ? 'Yes' : 'No') : ''),
        },
        {
            field: "failedDeliveryRemark", headerAlign: "left", hideSortIcons: true, headerName: "Failed Delivery Remark", flex: 1, minWidth: 160,
            valueGetter: (params) => params.row?.failedDeliveryRemark || '',
        },
        {
            field: "failedDeliveryTimestamp", headerAlign: "left", hideSortIcons: true, headerName: "Failed Delivery Timestamp", flex: 0.8, minWidth: 150,
            valueGetter: (params) => params.row?.failedDeliveryTimestamp || '',
        },
        {
            field: "rvpQcEnabled", headerAlign: "left", hideSortIcons: true, headerName: "RVP QC Enabled", flex: 0.6, minWidth: 120,
            valueGetter: (params) => params.row?.rvpQcEnabled || '',
        },
        {
            field: "action",
            headerAlign: "left",
            hideSortIcons: true,
            disableExport: true,
            headerName: "Action",
            flex: 0.6,
            minWidth: 100,
            renderCell: (params) => {
                const { row } = params;
                if (row.failedReason) return null;
                return (
                    <Box sx={cellStackSx}>
                        <Button variant="contained" size="small" sx={primaryBtnSx}
                            component={Link} to={`/order-detail?waybill=${row.waybill}`}>
                            Details
                        </Button>
                    </Box>
                );
            },
        },
    ];

    return (
        <>
            {rowSelectionModel.length > 0 && (
                <Stack direction='row' sx={{ mb: 1, justifyContent: 'flex-end' }}>
                    <LoadingButton loading={loading} onClick={bulkCreateHandler}
                        variant='contained' size="small" sx={primaryBtnSx}
                        startIcon={<CheckCircleIcon />}>
                        Download Labels
                    </LoadingButton>
                </Stack>
            )}
            <Box sx={dataGridWrapSx}>
                <ProzoDataGrid columns={columns}
                    autoHeight={true}
                    rows={listData.map((row, index) => ({ ...row, id: index }))}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    exportToExcel={true}
                    exportFileName={"Tracking List"}
                    page={page}
                    pagination={true}
                    hideFooterPagination={false}
                    checkboxSelection={true}
                    isRowSelectable={(params) => !params.row?.failedReason}
                    rowSelectionModel={rowSelectionModel}
                    setRowSelectionModel={setRowSelectionModel}
                    paginationMode={'client'}
                    rowCount={listData?.length || 0}
                    getRowId={(row) => row.waybill}
                />
            </Box>
        </>
    );
};
export default TrackingListitem;
